// src/pages/AttendancePage.jsx
// re-push trigger
import React, { useEffect, useState } from 'react';
import {
  Box, Button, TextField, MenuItem, Select, FormControl, InputLabel,
  Typography, Stack, Paper, Snackbar
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import TodayIcon from '@mui/icons-material/Today';
import { getTeacherLessons } from '../api/reportApi';
import {
  getLessonStudents,
  getAttendance,
  saveAttendance,
  getAttendanceNote,
  saveAttendanceNote
} from '../api/attendanceApi';
import {
  gregorianToJalaaliString,
  jalaaliStringToGregorianIso
} from '../utils/jalaaliHelper';

export default function AttendancePage() {
  const [lessons, setLessons] = useState([]);
  const [lessonId, setLessonId] = useState('');
  const [internalDateIso, setInternalDateIso] = useState(new Date().toISOString().slice(0, 10));
  const [jalaaliDateStr, setJalaaliDateStr] = useState(() =>
    gregorianToJalaaliString(new Date().toISOString().slice(0, 10))
  );
  const [students, setStudents] = useState([]);
  const [teacherNote, setTeacherNote] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '' });
  const [loading, setLoading] = useState(false);
  const [sessionIndex, setSessionIndex] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const arr = await getTeacherLessons();
        setLessons(Array.isArray(arr) ? arr : []);
      } catch (e) {
        setLessons([]);
        console.error('load lessons failed', e);
      }
    })();
  }, []);

  const onChangeJalaaliDate = (val) => {
    setJalaaliDateStr(val);
    const iso = jalaaliStringToGregorianIso(val);
    if (iso) setInternalDateIso(iso);
  };

  const onNativeDateChange = (e) => {
    const gIso = e.target.value;
    setInternalDateIso(gIso);
    setJalaaliDateStr(gregorianToJalaaliString(gIso));
  };

  const loadForLesson = async () => {
    if (!lessonId) return setSnack({ open: true, msg: 'یک درس انتخاب کنید' });
    setLoading(true);
    try {
      const studs = await getLessonStudents(lessonId);
      const normalized = (studs || []).map(s => ({
        id: s.Id ?? s.id,
        name: s.Name ?? s.name ?? `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim(),
        code: s.Code ?? s.code ?? s.StudentCode ?? ''
      }));
      const withStatus = normalized.map(s => ({ ...s, status: 'present', note: '' }));

      try {
        const att = await getAttendance(Number(lessonId), internalDateIso, sessionIndex);
        if (att && Array.isArray(att.items)) {
          const map = att.items.reduce((acc, it) => { acc[it.studentId] = it; return acc; }, {});
          withStatus.forEach(w => {
            if (map[w.id]) {
              w.status = map[w.id].status || w.status;
              w.note = map[w.id].note || '';
            }
          });
        }
      } catch (err) {
        console.error('attendance load failed', err);
        setSnack({ open: true, msg: 'خطا در بارگذاری حضور — وضعیت پیش‌فرض استفاده شد' });
      }

      setStudents(withStatus);

      try {
        const noteRes = await getAttendanceNote(Number(lessonId), internalDateIso, sessionIndex);
        setTeacherNote(noteRes?.note || '');
      } catch {
        setTeacherNote('');
      }
    } catch (err) {
      console.error('loadForLesson failed', err);
      setStudents([]);
      setSnack({ open: true, msg: 'لیست دانش‌آموزان بارگیری نشد' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!lessonId) return setSnack({ open: true, msg: 'یک درس انتخاب کنید' });

    const payload = {
      lessonId: Number(lessonId),
      date: internalDateIso,
      sessionIndex: Number(sessionIndex),
      items: students.map(s => ({ studentId: s.id, status: s.status, note: s.note }))
    };

    const token = localStorage.getItem('token');

    try {
      await saveAttendance(payload, token);
      await saveAttendanceNote({
        lessonId: Number(lessonId),
        date: internalDateIso,
        sessionIndex: Number(sessionIndex),
        note: teacherNote
      });
      setSnack({ open: true, msg: 'حضور و یادداشت ذخیره شد' });
    } catch (err) {
      console.error('save failed', err);
      let message = 'خطا در ذخیره حضور/یادداشت';
      if (err?.response?.data) {
        try { message = err.response.data.message || JSON.stringify(err.response.data); } catch {}
      } else if (err?.message) {
        message = err.message;
      }
      setSnack({ open: true, msg: message });
    }
  };

  const markAll = (status) => setStudents(prev => prev.map(s => ({ ...s, status })));

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: { xs: '100%', sm: 220 }, width: { xs: '100%', sm: 'auto' } }}>
            <InputLabel id="lesson-select-label">درس</InputLabel>
            <Select
              labelId="lesson-select-label"
              value={lessonId}
              label="درس"
              onChange={e => setLessonId(e.target.value)}
            >
              <MenuItem value=''>انتخاب کنید</MenuItem>
              {lessons.map(l => (
                <MenuItem key={l.id ?? l.Id} value={l.id ?? l.Id}>
                  {l.title ?? l.Title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="تاریخ هجری"
            aria-label="تاریخ هجری"
            value={jalaaliDateStr}
            onChange={(e) => onChangeJalaaliDate(e.target.value)}
            sx={{ width: { xs: '100%', sm: 160 } }}
            id="jalali-date"
            name="jalaliDate"
            size="small"
          />

          <TextField
            type="date"
            value={internalDateIso}
            onChange={onNativeDateChange}
            sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }}
            inputProps={{ 'aria-hidden': true }}
            size="small"
          />

          <FormControl sx={{ width: { xs: '100%', sm: 120 } }}>
            <InputLabel id="session-select-label">جلسه</InputLabel>
            <Select
              labelId="session-select-label"
              id="session-select"
              value={sessionIndex}
              label="جلسه"
              onChange={e => setSessionIndex(Number(e.target.value))}
              size="small"
            >
              <MenuItem value={1}>جلسه 1</MenuItem>
              <MenuItem value={2}>جلسه 2</MenuItem>
              <MenuItem value={3}>جلسه 3</MenuItem>
              <MenuItem value={4}>جلسه 4</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'stretch', sm: 'flex-start' } }} flexWrap="wrap">
            <Button fullWidth={false} variant="contained" onClick={loadForLesson} startIcon={<TodayIcon />}>بارگذاری</Button>
            <Button fullWidth={false} variant="outlined" onClick={() => markAll('present')}>همه حاضر</Button>
            <Button fullWidth={false} variant="outlined" onClick={() => markAll('absent')}>همه غایب</Button>
            <Button fullWidth={false} variant="contained" color="success" onClick={handleSave} startIcon={<SaveIcon />}>ذخیره</Button>
          </Stack>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          تاریخ میلادی ارسال‌شده به سرور: {internalDateIso} — نمایش هجری: {jalaaliDateStr} — جلسه: {sessionIndex}
        </Typography>
      </Paper>

      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>دانش‌آموزان</Typography>
        <Box>
          {students.map(s => (
            <Paper
              key={s.id}
              sx={{
                p: 1,
                my: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                minWidth: 0
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                  {s.name}
                  <Typography component="span" sx={{ ml: 1, color: 'text.secondary', wordBreak: 'keep-all' }}>#{s.code}</Typography>
                </Typography>
              </Box>

              <FormControl sx={{ minWidth: { xs: '100%', sm: 140 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel id={`status-label-${s.id}`}>وضعیت</InputLabel>
                <Select
                  labelId={`status-label-${s.id}`}
                  id={`status-${s.id}`}
                  name={`status-${s.id}`}
                  value={s.status}
                  label="وضعیت"
                  onChange={e => setStudents(prev => prev.map(x => x.id === s.id ? { ...x, status: e.target.value } : x))}
                  size="small"
                >
                  <MenuItem value="present">حاضر</MenuItem>
                  <MenuItem value="absent">غایب</MenuItem>
                  <MenuItem value="excused">موجه</MenuItem>
                  <MenuItem value="late">تاخیر</MenuItem>
                </Select>
              </FormControl>

              <TextField
                id={`note-for-${s.id}`}
                name={`note-${s.id}`}
                aria-label={`note-for-student-${s.id}`}
                placeholder="توضیح (اختیاری)"
                value={s.note}
                onChange={e => setStudents(prev => prev.map(x => x.id === s.id ? { ...x, note: e.target.value } : x))}
                sx={{ width: { xs: '100%', sm: 300 }, mt: { xs: 1, sm: 0 } }}
                size="small"
                multiline
              />
            </Paper>
          ))}
        </Box>
      </Box>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>یادداشت دبیر برای این جلسه</Typography>
        <TextField
          label="یادداشت"
          placeholder="کارهایی که انجام شد و برنامهٔ جلسهٔ بعد..."
          multiline
          minRows={3}
          fullWidth
          value={teacherNote}
          onChange={e => setTeacherNote(e.target.value)}
        />
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}